import { ArrowLeft, Check, Save, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createVariant, getAttributes, getServiceVariants } from '../../../api/service.api';
import { useToastContext } from '../../../contexts/ToastContext';
import styles from '../../../assets/scss/pages/ServiceVariantCreate.module.scss';

const UNIT_SUGGESTIONS = ['Cái', 'Lượt', 'Chai', 'Kg', 'Tạ', 'Tấn', 'Hộp', 'Bộ', 'Lọ', 'Tuýp'];

function normalize(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, '')
        .toLowerCase();
}

function MoneyInput({ value, onChange, placeholder, hasError }) {
    const inputRef = useRef(null);
    const displayValue = value > 0 ? Number(value).toLocaleString('vi-VN') : '';

    const handleChange = (e) => {
        const el = e.target;
        const prevVal = displayValue;
        const rawNew = el.value.replace(/\D/g, '');
        const num = rawNew ? parseInt(rawNew, 10) : 0;
        const newFormatted = num > 0 ? num.toLocaleString('vi-VN') : '';

        const cursorPos = el.selectionStart;
        const dotsBefore = (prevVal.slice(0, cursorPos).match(/\./g) || []).length;
        const rawCursorPos = cursorPos - dotsBefore;
        const dotsInNew = (pos) => {
            let count = 0, raw = 0;
            for (let i = 0; i < newFormatted.length && raw < pos; i++) {
                if (newFormatted[i] === '.') count++; else raw++;
            }
            return count;
        };
        const newCursor = rawCursorPos + dotsInNew(rawCursorPos);

        onChange(num);
        requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
        });
    };

    return (
        <input
            ref={inputRef}
            className={`${styles.formInput}${hasError ? ` ${styles.inputError}` : ''}`}
            value={displayValue}
            onChange={handleChange}
            placeholder={placeholder || '0'}
            inputMode="numeric"
        />
    );
}

export default function ServiceVariantCreate() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [serviceData, setServiceData] = useState(null);
    const [attributes, setAttributes] = useState([]);
    const [selectedValues, setSelectedValues] = useState({}); // { attributeId: valueId }
    const [formData, setFormData] = useState({
        sku: '',
        barcode: '',
        unit: '',
        importPrice: 0,
        capitalPrice: 0,
        retailPrice: 0,
        salePrice: 0,
        quantity: 0,
        notSell: 0,
        alert: 0,
        inStock: true,
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const { showError } = useToastContext();
    const [showUnitSuggestions, setShowUnitSuggestions] = useState(false);
    const [attrSearch, setAttrSearch] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [serviceRes, attrsRes] = await Promise.all([
                getServiceVariants(id),
                getAttributes(),
            ]);
            const service = serviceRes.data.variant || serviceRes.data;
            setServiceData({ name: service.name, typeService: service.typeService });
            setAttributes(attrsRes.data.attributes || []);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSelectValue = (attributeId, valueId) => {
        setSelectedValues((prev) => {
            if (prev[attributeId] === valueId) {
                const next = { ...prev };
                delete next[attributeId];
                return next;
            }
            return { ...prev, [attributeId]: valueId };
        });
    };

    const validate = () => {
        const errs = {};
        if (!formData.unit.trim()) errs.unit = 'Vui lòng nhập đơn vị';
        if (!formData.importPrice || formData.importPrice <= 0) errs.importPrice = 'Phải lớn hơn 0';
        if (!formData.retailPrice || formData.retailPrice <= 0) errs.retailPrice = 'Phải lớn hơn 0';
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            showError(Object.values(errs)[0] || 'Vui lòng kiểm tra lại thông tin');
            return;
        }
        setSaving(true);
        try {
            await createVariant({
                serviceId: id,
                ...formData,
                attributeValueIds: Object.values(selectedValues),
            });
            setShowSuccess(true);
            setTimeout(() => navigate(`/admin/services/${id}`), 1200);
        } catch (err) {
            const msg = err.response?.data?.message || 'Tạo biến thể thất bại';
            setErrors({ submit: msg });
            showError(msg);
        } finally {
            setSaving(false);
        }
    };

    const isProduct = serviceData?.typeService === 'sanpham';

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button className={styles.backBtn} onClick={() => navigate(-1)}>
                            <ArrowLeft />
                        </button>
                        <div>
                            <div className={styles.skeleton} style={{ width: 120, height: 14, borderRadius: 6, marginBottom: 6 }} />
                            <div className={styles.skeleton} style={{ width: 200, height: 26, borderRadius: 8 }} />
                        </div>
                    </div>
                    <div className={styles.skeleton} style={{ width: 140, height: 42, borderRadius: 10 }} />
                </div>
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 140, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.formGrid}>
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i} className={styles.formGroup}>
                                            <div className={styles.skeleton} style={{ width: 80, height: 14, borderRadius: 6 }} />
                                            <div className={styles.skeleton} style={{ height: 44, borderRadius: 10 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.layoutRight}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 100, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={styles.attrSection}>
                                        <div className={styles.skeleton} style={{ width: 70, height: 14, borderRadius: 6, marginBottom: 10 }} />
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {[60, 50, 70].map((w, j) => (
                                                <div key={j} className={styles.skeleton} style={{ width: w, height: 34, borderRadius: 999 }} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const filteredUnitSuggestions = UNIT_SUGGESTIONS.filter(
        (u) => !formData.unit || u.toLowerCase().includes(formData.unit.toLowerCase()),
    );

    const variantPreviewName = attributes
        .filter((a) => selectedValues[a.id])
        .map((a) => a.attributeValues?.find((v) => v.id === selectedValues[a.id])?.value)
        .filter(Boolean)
        .join(' - ');

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <div className={styles.headerSub}>{serviceData?.name || '...'}</div>
                        <h1 className={styles.title}>Tạo biến thể mới</h1>
                    </div>
                </div>
                <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
                    <Save size={18} />
                    {saving ? 'Đang lưu...' : 'Lưu biến thể'}
                </button>
            </div>

            {errors.submit && <div className={styles.errorMsg}>{errors.submit}</div>}

            <div className={styles.layout}>
                {/* ===== CỘT TRÁI: Form ===== */}
                <div className={styles.layoutLeft}>
                    {/* Thông tin & Giá */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin & Giá</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                {/* Đơn vị */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        Đơn vị <span>*</span>
                                    </label>
                                    <div className={styles.unitWrap}>
                                        <input
                                            className={`${styles.formInput}${errors.unit ? ` ${styles.inputError}` : ''}`}
                                            value={formData.unit}
                                            onChange={(e) => {
                                                setFormData((p) => ({ ...p, unit: e.target.value }));
                                                setErrors((p) => ({ ...p, unit: '' }));
                                            }}
                                            onFocus={() => setShowUnitSuggestions(true)}
                                            onBlur={() =>
                                                setTimeout(() => setShowUnitSuggestions(false), 150)
                                            }
                                            placeholder="VD: Cái, Lượt, Kg..."
                                        />
                                        {showUnitSuggestions && filteredUnitSuggestions.length > 0 && (
                                            <div className={styles.unitDropdown}>
                                                {filteredUnitSuggestions.map((u) => (
                                                    <button
                                                        key={u}
                                                        className={styles.unitOption}
                                                        onMouseDown={() => {
                                                            setFormData((p) => ({ ...p, unit: u }));
                                                            setErrors((p) => ({ ...p, unit: '' }));
                                                            setShowUnitSuggestions(false);
                                                        }}
                                                    >
                                                        {u}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {errors.unit && (
                                        <span className={styles.formError}>{errors.unit}</span>
                                    )}
                                </div>

                                {/* Giá nhập */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        Giá nhập <span>*</span>
                                    </label>
                                    <MoneyInput
                                        value={formData.importPrice}
                                        onChange={(v) => {
                                            setFormData((p) => ({ ...p, importPrice: v }));
                                            setErrors((p) => ({ ...p, importPrice: '' }));
                                        }}
                                        placeholder="0"
                                        hasError={!!errors.importPrice}
                                    />
                                    {errors.importPrice && (
                                        <span className={styles.formError}>{errors.importPrice}</span>
                                    )}
                                </div>

                                {/* Giá bán */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        Giá bán <span>*</span>
                                    </label>
                                    <MoneyInput
                                        value={formData.retailPrice}
                                        onChange={(v) => {
                                            setFormData((p) => ({ ...p, retailPrice: v }));
                                            setErrors((p) => ({ ...p, retailPrice: '' }));
                                        }}
                                        placeholder="0"
                                        hasError={!!errors.retailPrice}
                                    />
                                    {errors.retailPrice && (
                                        <span className={styles.formError}>{errors.retailPrice}</span>
                                    )}
                                </div>

                                {/* Giá khuyến mãi */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Giá khuyến mãi</label>
                                    <MoneyInput
                                        value={formData.salePrice}
                                        onChange={(v) =>
                                            setFormData((p) => ({ ...p, salePrice: v }))
                                        }
                                        placeholder="0 (không có)"
                                    />
                                </div>

                                {/* SKU */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Mã SKU</label>
                                    <input
                                        className={styles.formInput}
                                        value={formData.sku}
                                        onChange={(e) =>
                                            setFormData((p) => ({ ...p, sku: e.target.value }))
                                        }
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>

                                {/* Barcode */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Mã vạch</label>
                                    <input
                                        className={styles.formInput}
                                        value={formData.barcode}
                                        onChange={(e) =>
                                            setFormData((p) => ({ ...p, barcode: e.target.value }))
                                        }
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Tồn kho — chỉ cho sản phẩm */}
                    {isProduct && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>Tồn kho</div>
                            <div className={styles.cardBody}>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Số lượng tồn kho</label>
                                        <input
                                            className={styles.formInput}
                                            type="number"
                                            min="0"
                                            value={formData.quantity || ''}
                                            onChange={(e) =>
                                                setFormData((p) => ({
                                                    ...p,
                                                    quantity: parseInt(e.target.value) || 0,
                                                }))
                                            }
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Không bán</label>
                                        <input
                                            className={styles.formInput}
                                            type="number"
                                            min="0"
                                            value={formData.notSell || ''}
                                            onChange={(e) =>
                                                setFormData((p) => ({
                                                    ...p,
                                                    notSell: parseInt(e.target.value) || 0,
                                                }))
                                            }
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Cảnh báo tồn kho</label>
                                        <input
                                            className={styles.formInput}
                                            type="number"
                                            min="0"
                                            value={formData.alert || ''}
                                            onChange={(e) =>
                                                setFormData((p) => ({
                                                    ...p,
                                                    alert: parseInt(e.target.value) || 0,
                                                }))
                                            }
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ===== CỘT PHẢI: Thuộc tính ===== */}
                <div className={styles.layoutRight}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span>Thuộc tính</span>
                            {Object.keys(selectedValues).length > 0 && (
                                <span className={styles.attrCount}>
                                    {Object.keys(selectedValues).length} đã chọn
                                </span>
                            )}
                        </div>
                        <div className={styles.cardBody}>
                            {attributes.length === 0 ? (
                                <div className={styles.attrEmpty}>
                                    Chưa có thuộc tính nào.
                                </div>
                            ) : (
                                <>
                                    <div className={styles.attrSearchWrap}>
                                        <Search size={16} />
                                        <input
                                            className={styles.attrSearchInput}
                                            value={attrSearch}
                                            onChange={(e) => setAttrSearch(e.target.value)}
                                            placeholder="Tìm thuộc tính..."
                                        />
                                        {attrSearch && (
                                            <button className={styles.attrSearchClear} onClick={() => setAttrSearch('')}>
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {attributes
                                        .filter((attr) => {
                                            if (!attrSearch.trim()) return true;
                                            const q = normalize(attrSearch);
                                            if (normalize(attr.name).includes(q)) return true;
                                            return (attr.attributeValues || []).some((v) =>
                                                normalize(v.value).includes(q),
                                            );
                                        })
                                        .map((attr) => (
                                            <div key={attr.id} className={styles.attrSection}>
                                                <div className={styles.attrTitle}>{attr.name}</div>
                                                <div className={styles.attrChips}>
                                                    {(attr.attributeValues || []).map((val) => {
                                                        const isSelected =
                                                            selectedValues[attr.id] === val.id;
                                                        return (
                                                            <button
                                                                key={val.id}
                                                                className={`${styles.attrChip}${isSelected ? ` ${styles.attrChipActive}` : ''}`}
                                                                onClick={() =>
                                                                    handleSelectValue(
                                                                        attr.id,
                                                                        val.id,
                                                                    )
                                                                }
                                                            >
                                                                {isSelected && <Check size={12} />}
                                                                {val.value}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                </>
                            )}

                            {variantPreviewName && (
                                <div className={styles.selectedSummary}>
                                    <div className={styles.selectedSummaryLabel}>Tên biến thể</div>
                                    <div className={styles.selectedSummaryName}>
                                        {variantPreviewName}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Tạo biến thể thành công!</span>
                    </div>
                </div>
            )}
        </div>
    );
}
